import { ConsentGroup, parseGroupString, type ConsentState } from "./groups";

/** Dispatched by OneTrust on save, and by our `OptanonWrapper` after init. */
const GROUPS_UPDATED_EVENT = "OneTrustGroupsUpdated";

declare global {
  interface Window {
    /** Comma-wrapped active category ids, e.g. ",1,3,". */
    OnetrustActiveGroups?: string;
    OneTrust?: {
      ToggleInfoDisplay?: () => void;
      /** True once the visitor has actually dismissed the banner. */
      IsAlertBoxClosed?: () => boolean;
    };
  }
}

/**
 * In opt-out geographies OneTrust pre-populates `OnetrustActiveGroups` with
 * every category *before* the visitor chooses anything, so reading consent from
 * the group string alone would treat a pre-populated default as full consent.
 * Asking the SDK whether the banner was dismissed is what keeps us honest.
 *
 * We ask `IsAlertBoxClosed()` rather than reading `OptanonAlertBoxClosed`
 * directly, because the cookie name is not reliable on a subdomain. Where a
 * parent domain runs its own OneTrust property, its unsuffixed cookie is scoped
 * to `.parent.tld` and visible to us, while our own script writes a suffixed
 * variant (`OptanonAlertBoxClosed_<id>`) — so a name lookup can silently answer
 * with the parent site's decision. The SDK only ever answers for the script
 * that is actually running here.
 *
 * Undecided until the SDK exists: no `window.OneTrust` means either it hasn't
 * initialised yet or it was blocked, and both should load nothing. Our
 * `OptanonWrapper` re-broadcasts after init, which brings us back through here.
 */
export function readOneTrustConsent(): ConsentState {
  if (typeof window === "undefined") {
    return { decided: false, granted: new Set([ConsentGroup.Necessary]) };
  }

  const decided = window.OneTrust?.IsAlertBoxClosed?.() === true;
  return { decided, granted: parseGroupString(window.OnetrustActiveGroups) };
}

export function subscribeOneTrust(onChange: () => void): () => void {
  if (typeof window === "undefined") return () => {};

  window.addEventListener(GROUPS_UPDATED_EVENT, onChange);
  return () => window.removeEventListener(GROUPS_UPDATED_EVENT, onChange);
}

/** No-op when the OneTrust script was blocked — better than throwing at a click. */
export function openOneTrustPreferences(): void {
  if (typeof window === "undefined") return;
  window.OneTrust?.ToggleInfoDisplay?.();
}
